import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { SiteLayout } from "@/components/SiteLayout";
import api, { formatApiError } from "@/lib/api";
import { CONTACT } from "@/constants/testIds";

const monoLabel = { fontFamily: "'DM Mono', monospace", fontSize: 11, letterSpacing: "0.14em", color: "#0891b2", textTransform: "uppercase", marginBottom: 14 };

const STEPS = [
  { num: "01", title: "We review your request", desc: "Within one business day, an ARGOV governance specialist reviews your AI environment description and prepares a tailored session." },
  { num: "02", title: "30-minute discovery call", desc: "We map your current AI estate, identify your highest-risk assets against your sector's regulatory obligations, and show you what ARGOV would govern." },
  { num: "03", title: "Custom framework mapping", desc: "You receive a personalised mapping of your AI assets to NIST AI RMF, ISO 42001, UAE PDPL, and NESA — with ARGOV's coverage shown against each gap." },
];

const SECTORS = [
  { v: "", l: "Select your sector" },
  { v: "government", l: "UAE Federal / Emirate Government" },
  { v: "bfsi", l: "Banking, Financial Services & Insurance" },
  { v: "healthcare", l: "Healthcare & Life Sciences" },
  { v: "energy", l: "Energy & Critical Infrastructure" },
  { v: "telecom", l: "Telecommunications" },
  { v: "other", l: "Other regulated enterprise" },
];

const emptyForm = { name: "", email: "", company: "", role: "", sector: "", environment: "", website: "" };

export default function ContactPage() {
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [mountedAt] = useState(() => Date.now());

  useEffect(() => { setError(""); }, [form]);

  const canSubmit = form.name.trim().length >= 2 && /.+@.+\..+/.test(form.email);

  const change = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      const payload = {
        ...form,
        sector: form.sector || "",
        form_ms: Date.now() - mountedAt,
      };
      await api.post("/leads", payload);
      setSubmitted(true);
    } catch (err) {
      const msg = err?.response?.status === 429
        ? "Too many requests. Please wait a moment and try again."
        : formatApiError(err?.response?.data?.detail) || err.message;
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SiteLayout>
      <section style={{ background: "#f2f5f8", padding: "80px 48px", minHeight: "calc(100vh - 120px)" }}>
        <div style={{ maxWidth: 1280, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 64, alignItems: "start" }}>
          <div>
            <p style={monoLabel}>REQUEST A DEMO</p>
            <h1 style={{ fontSize: 44, fontWeight: 800, letterSpacing: "-0.03em", color: "#0c1a2e", lineHeight: 1.1, marginBottom: 20 }}>See ARGOV against your own AI environment.</h1>
            <p style={{ fontSize: 16, color: "#3d5166", lineHeight: 1.7, marginBottom: 36 }}>A 30-minute discovery call — we map your AI estate, identify your highest-risk assets, and show you what ARGOV would govern and enforce before you commit to anything.</p>

            <div style={{ display: "flex", flexDirection: "column", gap: 20, marginBottom: 40 }}>
              {STEPS.map((s) => (
                <div key={s.num} style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
                  <div style={{ width: 36, height: 36, borderRadius: "50%", background: "#0891b2", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <span className="font-mono" style={{ fontSize: 12, fontWeight: 700, color: "#fff" }}>{s.num}</span>
                  </div>
                  <div>
                    <p style={{ fontSize: 15, fontWeight: 700, color: "#0c1a2e", marginBottom: 4 }}>{s.title}</p>
                    <p style={{ fontSize: 13.5, color: "#4a6070", lineHeight: 1.6 }}>{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ background: "#fff", border: "1px solid #e2eaf2", borderRadius: 12, padding: 24 }}>
              <p className="font-mono" style={{ fontSize: 10, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 14 }}>TRUSTED BY</p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                {["UAE Federal Entities", "GCC Banks & BFSI", "Healthcare Providers", "Critical Infrastructure", "Telecom Operators"].map((t) => (
                  <span key={t} style={{ fontSize: 12, color: "#5a6f82", background: "#f1f5f9", border: "1px solid #dde6f2", borderRadius: 5, padding: "4px 12px" }}>{t}</span>
                ))}
              </div>
              <p style={{ fontSize: 12, color: "#94a3b8", marginTop: 14, fontStyle: "italic" }}>We reply within one business day.</p>
            </div>
          </div>

          <div style={{ background: "#fff", border: "1px solid #e2eaf2", borderRadius: 16, padding: 40, boxShadow: "0 4px 24px rgba(12,26,46,0.07)" }}>
            <h2 style={{ fontSize: 22, fontWeight: 700, color: "#0c1a2e", marginBottom: 6, letterSpacing: "-0.02em" }}>Demo request</h2>
            <p style={{ fontSize: 13.5, color: "#6b7280", marginBottom: 24, lineHeight: 1.5 }}>Tell us about your AI environment and governance priorities.</p>

            {submitted ? (
              <div data-testid={CONTACT.success} style={{ textAlign: "center", padding: "32px 0" }}>
                <div style={{ width: 64, height: 64, background: "#dcfce7", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", fontSize: 28, color: "#16a34a" }}>✓</div>
                <h3 style={{ fontSize: 22, fontWeight: 700, color: "#0c1a2e", marginBottom: 10 }}>Request received</h3>
                <p style={{ fontSize: 15, color: "#4a6070", lineHeight: 1.65, marginBottom: 24 }}>Thank you — we will come back within one business day to schedule your discovery call.</p>
                <Link to="/" style={{ fontSize: 14, color: "#0891b2", fontWeight: 600 }}>← Return to platform overview</Link>
              </div>
            ) : (
              <form data-testid={CONTACT.form} onSubmit={onSubmit} style={{ display: "flex", flexDirection: "column", gap: 18 }} noValidate>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                  <Field label="Full name *"><input data-testid={CONTACT.name} type="text" required placeholder="Your full name" value={form.name} onChange={change("name")} style={inputStyle} /></Field>
                  <Field label="Work email *"><input data-testid={CONTACT.email} type="email" required placeholder="name@company.com" value={form.email} onChange={change("email")} style={inputStyle} /></Field>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                  <Field label="Company"><input data-testid={CONTACT.company} type="text" placeholder="Your organisation" value={form.company} onChange={change("company")} style={inputStyle} /></Field>
                  <Field label="Your role"><input data-testid={CONTACT.role} type="text" placeholder="CISO, CDO, CRO..." value={form.role} onChange={change("role")} style={inputStyle} /></Field>
                </div>
                <Field label="Industry sector">
                  <select data-testid={CONTACT.sector} value={form.sector} onChange={change("sector")} style={{ ...inputStyle, cursor: "pointer" }}>
                    {SECTORS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
                  </select>
                </Field>
                <Field label="Your AI environment">
                  <textarea data-testid={CONTACT.environment} rows={3} placeholder="Describe your current AI deployments, governance challenges, or regulatory obligations..." value={form.environment} onChange={change("environment")} style={{ ...inputStyle, resize: "vertical", lineHeight: 1.6 }} />
                </Field>
                {/* Honeypot — visually hidden, aria-hidden, tab-out */}
                <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", top: "auto", width: 1, height: 1, overflow: "hidden" }}>
                  <label>Website</label>
                  <input data-testid={CONTACT.honeypot} tabIndex={-1} autoComplete="off" type="text" name="website" value={form.website} onChange={change("website")} />
                </div>
                {error && <p data-testid={CONTACT.error} style={{ fontSize: 13, color: "#dc2626", background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.2)", borderRadius: 8, padding: "10px 12px" }}>{error}</p>}
                <button data-testid={CONTACT.submit} type="submit" disabled={!canSubmit || submitting} style={{
                  width: "100%", padding: 13, background: canSubmit ? "#0891b2" : "#cbd5e1", color: "#fff", border: "none", borderRadius: 8,
                  fontSize: 15, fontWeight: 600, cursor: canSubmit && !submitting ? "pointer" : "not-allowed",
                  fontFamily: "'DM Sans', sans-serif", transition: "background 0.15s",
                }}>
                  {submitting ? "Sending..." : canSubmit ? "Send request →" : "Enter name and email to continue"}
                </button>
                <p style={{ fontSize: 12, color: "#94a3b8", textAlign: "center", lineHeight: 1.55 }}>
                  By submitting, you consent to ARGOV processing your details to respond to this enquiry, in accordance with our <Link to="/privacy" style={{ color: "#0891b2" }}>Privacy Policy</Link> and <Link to="/terms" style={{ color: "#0891b2" }}>Terms</Link>. No legal or compliance advice is provided. Marketing communications require a separate opt-in.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}

const inputStyle = { width: "100%", padding: "10px 14px", border: "1.5px solid #e2eaf2", borderRadius: 8, fontSize: 14, color: "#0c1a2e", background: "#f8fafc", outline: "none", fontFamily: "'DM Sans', sans-serif" };

const Field = ({ label, children }) => (
  <div>
    <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6 }}>{label}</label>
    {children}
  </div>
);
